import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { fileStatusTypes, uploadSingleFile } from "global/cnr/reducers/UploadReducer";
import { createThumbnailIcons } from "global/components/imaging/createManifestIcons";
import { convertCanvasToFile } from "global/components/imaging/imageHelpers";
import { gEnums } from "global/enums/globalEnums";
import { deleteStorageProfileFolder } from "global/storage/removeStorage";
import { _storageSettings } from "global/storage/storageHelpers";
import { useCallback, useContext, useMemo } from "react";
import { getManifestPath } from "../ManifestContext";
import { ParentContext } from "../ParentContext";
import { UploadContext } from "../UploadContext";
import { useAppUserAccess } from "./useAppUserAccess";
import { FrameworkContext } from "../FrameworkContent";
import { LongPressContext } from "global/wrappers/LongPressWrapper";
import { _cacheGroupTypes } from "./useTimestamp";
import { convertHelpers } from "global/common/convert";

export const _allowUpload = true

export const useUpload = () => {

  // parentContext
  const parentContext = useContext(ParentContext);
  const { states, fns } = parentContext ?? {}
  const { app_state, paps_state } = states
  const { pathViews } = paps_state ?? {}
  const { events: eventKey } = pathViews ?? {}
  const { mainStorage } = app_state ?? {}
  const { database_fns } = fns

  const appUserAccess = useAppUserAccess()

  const longPressContext = useContext(LongPressContext)
  const { longPress_state } = longPressContext ?? {}
  const { appImage } = longPress_state ?? {}

  const frameworkContext = useContext(FrameworkContext)
  const { framework_state } = frameworkContext ?? {}
  const { currentLongPress } = framework_state ?? {}

  // uploadContext
  const uploadContext = useContext(UploadContext);
  const { upload_state } = uploadContext ?? {}

  const { productionSettings, storageRef, files_info } = upload_state ?? {}
  const { selectedFolderFiles } = files_info ?? {}

  const storageRefProd = useMemo(() => {
    let _storageRefProd = '';
    if (productionSettings && productionSettings.clientKey && storageRef) {
      const sr = storageRef.split('/')
      sr.forEach((s, index) => {
        switch (index) {
          case 1:
            _storageRefProd += productionSettings.clientKey ? productionSettings.clientKey : s
            break;
          default:
            _storageRefProd += s
        }
        if (index < sr.length - 1) {
          _storageRefProd += '/'
        }
      })
    }

  })

  const handleUpload_profiles = useCallback(
    async ({
      fileList,
      useEmail,
      uploadPath,
      callback,
    }) => {
      if (fileList) {
        const updateList = getUploadApprovedList(fileList, useEmail)
        if (!_.isEmpty(updateList)) {
          let uploaded;
          uploaded = await uploadAllFilesPromise(mainStorage, {
            appUserAccess,
            callback,
            eventKey,
            fileOptionType: gEnums.fileOptionTypes.clientProfile,
            files: updateList,
            uploadPath,
            pathViews,
          }, database_fns)
        }
      }
    },
    []
  );

  // ---------------------------------------------------------
  // MANUAL UPLOAD
  // ---------------------------------------------------------
  const handleUpload_manual = useCallback(
    async ({
      altItemData,
      appImage,
      callback,
      canvas,
      currentPageData,
      formData,
      handleStartUpdate,
      manifestFiles,
      storageLocationType,
      storageType,
      uploadPath,
      useAvatar,
      fileOptionType,
      fileManagmentOpts,
    }) => {

      const callback_afterUpload = () => {
        callback && callback()
      }

      console.log('appImage', appImage)

      // -----------------------------------------------------
      // AVATAR CANVAS UPLOAD
      // -----------------------------------------------------
      if (useAvatar && canvas) {
        const file = convertCanvasToFile(canvas);

        if (file) {
          // Mutates file.name
          getFileName(
            file,
            storageLocationType,
            selectedFolderFiles,
            altItemData,
            formData
          );

          file.metadata = { name: "test" };

          const sfp = {
            file,
            folderName: file.name,
            appUserAccess,
            storageRef,
            handleStartUpdate,
            storageRefProd,
            formData,
          };

          uploadSingleFile(mainStorage, sfp);
        }

        return;
      }

      // -----------------------------------------------------
      // NON-AVATAR HANDLING
      // -----------------------------------------------------
      switch (storageType) {
        case gEnums.storageTypes.manifest:
          // Upload the manifest files
          handleUpload_manifest({ mainStorage, pathViews, manifestFiles, appUserAccess, callback: callback_afterUpload });
          return;

        default:
          const _formData = currentPageData ?? formData;
          const _asf = getUpdateList(selectedFolderFiles, currentPageData);
          let uploaded;

          switch (storageLocationType) {
            case gEnums.storageLocationTypes.clientProfile:
              await deleteStorageProfileFolder(mainStorage, storageRef);
              uploaded = await uploadAllFilesPromise(mainStorage, {
                action: {},
                appImage,
                appUserAccess,
                autoReduceImage: true,
                callback: callback_afterUpload,
                currentPageData,
                eventKey,
                fileOptionType: fileOptionType || gEnums.fileOptionTypes.global,
                files: _asf,
                formData: _formData,
                handleStartUpdate,
                ignoreFileKey: true,
                storageLocationType,
                storageRefProd,
                uploadPath,
                pathViews,
                fileManagmentOpts
              }, database_fns);
              return;

            default:
              uploaded = await uploadAllFilesPromise(mainStorage, {
                action: {},
                appImage,
                appUserAccess,
                autoReduceImage: true,
                callback: callback_afterUpload,
                currentPageData,
                eventKey,
                fileOptionType,
                files: _asf,
                formData: _formData,
                handleStartUpdate,
                ignoreFileKey: true,
                pathViews,
                storageLocationType,
                storageRef,
                storageRefProd,
                uploadPath,
                fileManagmentOpts
              }, database_fns);
              return;
          }
      }
    },
    [
      selectedFolderFiles,
      appUserAccess,
      storageRef,
    ]
  );

  return { handleUpload_manual, handleUpload_profiles };
};


const getFileName = (file, storageLocationType, selectedFolderFiles, altItemData, formData, isThumbnail) => {
  let fileName;
  switch (storageLocationType) {
    case gEnums.storageLocationTypes.clientProfile:
    case gEnums.storageLocationTypes.clientProfiles:
    case gEnums.storageLocationTypes.pages:
    case gEnums.storageLocationTypes.profileGallery:
    case gEnums.storageLocationTypes.profiles:
      fileName = isThumbnail ? 'thumbnail' : 'full'
      break;
    case gEnums.storageLocationTypes.pagePdf:
      fileName = formData.name
      break;
    default:
      if (selectedFolderFiles) {
        fileName = selectedFolderFiles[0].name
      } else {
        fileName = isThumbnail ? 'thumbnail' : 'full'
      }
  }
  if (altItemData && altItemData.email) { fileName = altItemData.email }
  return {
    fileNameActual: getNameNoExt(file.name),
    fileName
  }
}

const getUpdateList = (selectedFolderFiles, currentPageData) => {
  const updateList = {}
  selectedFolderFiles.forEach(sff => {
    updateList[sff.name] = {
      file: {
        name: sff.name,
        fileImage: sff.fileImage
      },
      item: { ...currentPageData }
    }
  })
  return updateList
}

const getNameNoExt = (name) => {
  return name.replace(/\.(png|jpe?g|gif|bmp|webp|tiff?|heic)$/i, '');
};

const uploadAllFilesPromise = async (mainStorage, props, database_fns) => {

  const {
    action,
    appImage,
    appUserAccess,
    callback,
    eventKey,
    // fileOptionType,
    files,
    formData,
    handleStartUpdate,
    storageLocationType,
    uploadPath,
    pathViews,
    fileManagmentOpts
  } = props;


  const { fileOptionType, storageType } = fileManagmentOpts ?? {}

  const uploadAll = !action?.fileKey;
  if (_.isEmpty(files) || (!uploadAll && !action?.fileKey)) return;

  const metadata = {
    customMetadata: {
      uploadedBy: appUserAccess.email,
      ...(formData?.name && { name: formData.name }),
    },
  };

  // ⭐ Collect all URLs here
  const uploadedItems = [];

  await Promise.all(
    _.map(files, async (fileItem, fileKey) => {

      // fileKey = the name of the file...

      const allowItem = uploadAll || fileKey === action?.fileKey;
      if (!allowItem) return;

      handleStartUpdate?.({ fileKey });

      // file is an object
      const { file, item } = fileItem;

      // the item MAY NOT exist
      const { name, firstName, lastName, email } = item ?? {}

      const { fileImage } = file;
      const { name: file_name } = fileImage ?? {}

      // file_name === fileKey

      const fileName_nox = getNameNoExt(file_name)

      const updatedMetadata = {
        ...metadata,
        customMetadata: {
          ...metadata.customMetadata,
          ...(name && { name: name }),
          ...(email && { email: email }),
          ...(firstName && lastName && { name: `${firstName} ${lastName}` }),
        },
      };

      if (fileImage?.type === "application/pdf" || fileImage?.fileImage?.type === "application/pdf") {
        updatedMetadata.contentType = "application/pdf";
      }

      // storage path prefixing
      const prefix = _.includes(
        [
          gEnums.storageLocationTypes.pageGallery,
          gEnums.storageLocationTypes.profileGallery,
          gEnums.storageLocationTypes.clientProfiles,
        ],
        storageLocationType
      )
        ? `${fileKey}/`
        : "";

      // Upload a single file
      const uploadFile = async (f, nameOverride = null) => {


        const { fileInfo, baseFileName, fileName_nox } = f

        // const fileName_noxx = getNameNoExt(file_name)

        const fileName = nameOverride || fileName_nox || fileInfo.name;
        // const uploadPath = 

        let fullPath = `${uploadPath}/`

        switch (fileOptionType) {
          case gEnums.fileOptionTypes.global:
            fullPath += `${fileName}/${baseFileName}`
            break;
          case gEnums.fileOptionTypes.pages:
            // fullPath += `${fileName}${baseFileName}`
            fullPath += `${baseFileName}`
            break;
          case gEnums.fileOptionTypes.clientProfile:
            fullPath += `${email}/${baseFileName}`
            break;
          default:
            fullPath = `${name}/${baseFileName}`
        }

        // if (uploadPath) {
        //   fullPath = `${uploadPath}/${baseFileName}`
        // }

        console.group('uploadAllFilesPromise')
        console.log('fullPath', fullPath)
        console.log('fileInfo', fileInfo)
        console.groupEnd()

        if (_allowUpload) {
          const storageReference = ref(mainStorage, fullPath);
          const result = await uploadBytes(storageReference, fileInfo, updatedMetadata);
          const url = await getDownloadURL(result.ref);
          // ⭐ Push the result into the array
          uploadedItems.push({
            fileKey,
            fileName,
            url,
            fullPath: result.metadata.fullPath,
            prefix,
            fileName_nox,
            fileOptionType,
            eventKey,
          });
        } else {
          uploadedItems.push({
            fileKey,
            fileName,
            url: 'pending',
            fullPath: 'pending',
            prefix,
            fileName_nox,
            fileOptionType,
            eventKey,
          });
        }
      };

      // PDF case
      if (fileImage?.type === "application/pdf" || fileImage?.fileImage?.type === "application/pdf") {
        await uploadFile(fileImage.fileImage);
        return;
      }

      // Non-PDF case (images + thumbnails) s
      const thumbnailResults = await createThumbnailIcons(fileImage, true, true);

      const allFiles = [];

      _.forEach(thumbnailResults, (thumbnailResult) => {
        const isThumbnail = _.includes(thumbnailResult?.name, "thumb_");
        let { fileName, fileNameActual } = getFileName(thumbnailResult, storageLocationType, null, null, formData, isThumbnail, fileOptionType);
        if (!fileName && fileImage.name) fileName = fileImage.name;
        const baseFileName = getNameNoExt(fileName)
        let fileFolder = 'full'
        if (isThumbnail) {
          // fileName += _storageSettings.thumbSuffix;
          fileFolder = 'thumbnail'
        }

        allFiles.push({
          fileInfo: new File([thumbnailResult],
            fileName,
            { type: thumbnailResult.type }),
          baseFileName,
          fileFolder,
          fileNameActual,
          fileName_nox
        });
      });

      await Promise.all(_.map(allFiles, (f) => uploadFile(f)));
    })
  );

  // ⭐ Fire callback AFTER all uploads and URL collection is complete

  if (uploadedItems) {
    await ammendDatabase_assetUrls(pathViews, eventKey, fileManagmentOpts, uploadedItems, metadata, database_fns)
  }

  if (callback) callback(action, uploadedItems);

  // ⭐ return array of { fileKey, fileName, url, fullPath }
  return uploadedItems;
};

const buildBaseObject2 = (keys) => _.set({}, keys.join('.'), {});
const buildBaseObject = (keys) => _.set({}, keys, {});


const ammendDatabase_assetUrls = async (pathViews, eventKey, fileManagmentOpts, uploadedItems, metadata, database_fns) => {

  const _timestamp = Date.now()

  const { fileOptionType, storageType, viewItemItem, viewItemType } = fileManagmentOpts

  let _cacheGroupType = _cacheGroupTypes.urls
  let useDash = false
  // const datasToUpdate = [];

  const ddttuu = {}

  const _fps = uploadedItems[0]?.fullPath.split('/')

  const _fps_profile = {
    clientKey: pathViews.clients,
    eventKey: pathViews.events,
    profileKey: _fps[3],
    thumbnailOrFull: _fps[4],
  }

  const _fps_global = {
    clientKey: pathViews.clients,
    eventKey: pathViews.events,
    // uivi: _fps[5],
    uiviKey: _fps[6],
  }

  const _fps_pages = {
    clientKey: pathViews.clients,
    eventKey: pathViews.events,
    uivi: _fps[5],
    uiviKey: _fps[6],
  }

  const _pageType = _fps_pages.uivi || viewItemType
  const _pageKey = _fps_pages.uiviKey || viewItemItem?._itemKey

  const refPaths = {
    clientProfile: `clients/${_fps_profile.clientKey}/profileGallery/galleryItems/`,
    global: `clients/${_fps_pages.clientKey}/events/${_fps_pages.eventKey}/_assetUrls/global/`,
    pages: `clients/${_fps_pages.clientKey}/events/${_fps_pages.eventKey}/_assetUrls/pages/`,
  }

  const refPaths_ts = {
    clientProfile: `clients/${_fps_profile.clientKey}/events/${_fps_profile.eventKey}/_assetUrls/_timestamp/`,
    global: `clients/${_fps_pages.clientKey}/events/${_fps_pages.eventKey}/_assetUrls/_timestamp/`,
    pages: `clients/${_fps_pages.clientKey}/events/${_fps_pages.eventKey}/_assetUrls/_timestamp/`,
  }

  const objectPaths = {
    clientProfile: [_fps_profile.profileKey],
    global: [storageType, _fps_global.uiviKey],
    pages: [_pageType, _pageKey]
  }

  const baseObjects = {
    clientProfile: buildBaseObject(objectPaths.clientProfile),
    global: buildBaseObject(objectPaths.global),
    pages: buildBaseObject(objectPaths.pages)
  }

  _.forEach(uploadedItems, (uploadedItem) => {
    const { fullPath, url } = uploadedItem;
    const fps = fullPath.split('/');
    const fps_last = fps[fps.length - 1]

    switch (fileOptionType) {
      case gEnums.fileOptionTypes.clientProfile:
        _.set(
          baseObjects.clientProfile,
          [...objectPaths.clientProfile, fps_last].join('.'),
          url
        );
        _cacheGroupType = _cacheGroupTypes.appUsersInfo
        break;

      case gEnums.fileOptionTypes.global:
        _.set(
          baseObjects.global,
          [...objectPaths.global, fps_last].join('.'),
          url
        );
        break;

      case gEnums.fileOptionTypes.pages:
        _.set(
          baseObjects.pages,
          [...objectPaths.pages, fps_last].join('.'),
          url
        );
        break;

      default:
      // Safe because no keys contain dots in this branch 
      // /clients/clientKey/events/eventKey/_assetUrls/pages

    }
  });



  let refPath = null;
  let refPath_ts = null;
  let _dataToUpdate = {};

  const dataTs = {
    timestamp: _timestamp
  }

  switch (fileOptionType) {
    case gEnums.fileOptionTypes.clientProfile:
      refPath = refPaths.clientProfile;
      refPath_ts = refPaths_ts.clientProfile;
      _dataToUpdate = expandDotNotationWithFileFix(baseObjects.clientProfile)
      break;

    case gEnums.fileOptionTypes.global:
      refPath = refPaths.global;
      refPath_ts = refPaths_ts.global;
      _dataToUpdate = expandDotNotationWithFileFix(baseObjects.global)
      break;

    case gEnums.fileOptionTypes.pages:
      refPath = refPaths.pages;
      refPath_ts = refPaths_ts.pages;
      _dataToUpdate = expandDotNotationWithFileFix(baseObjects.pages)
      break;

    default:
    // Safe because no keys contain dots in this branch 
    // /clients/clientKey/events/eventKey/_assetUrls/pages

  }

  const _dtu = _dataToUpdate

  console.log('ddttuu', ddttuu)

  console.group('ammendDatabase_assetUrls')
  console.log('_dataToUpdate', refPath, _dtu)
  console.log('dataTs', refPath_ts, dataTs)
  console.log('update_timestamp', _cacheGroupType, eventKey, _timestamp)
  console.groupEnd()

  if (_allowUpload) {
    await database_fns.set_doc(refPath, _dtu, true)
    await database_fns.set_doc(refPath_ts, dataTs, true)
    await database_fns.update_timestamp(_cacheGroupType, eventKey, _timestamp)
  }


};

// const buildFirestoreUpdate = (keys, values) => _.mapKeys(values, (v, k) => `${keys.join('.')}.${k}`); 
// const nestedData = useDash ? expandDotNotationWithFileFix(_dataToUpdate, '_') : expandDotNotationWithFileFix(dataToUpdate);

function stripImageSuffix(name) {
  return name.replace(/\.(jpg|jpeg|png|gif)$/i, ''); // remove image extension
}

function expandDotNotationWithFileFix(obj, splitChar) {
  const result = {};
  for (const key in obj) {
    const parts = key.split(splitChar ? splitChar : '.');

    // Detect if the first part contains an image extension
    if (parts.length > 1) {
      parts[0] = stripImageSuffix(parts[0]);
    }

    let current = result;
    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];
      if (i === parts.length - 1) {
        current[part] = obj[key];
      } else {
        current[part] = current[part] || {};
        current = current[part];
      }
    }
  }
  return result;
}

const handleUpload_manifest = ({ mainStorage, pathViews, manifestFiles, appUserAccess, callback }) => {
  let smp2 = getManifestPath(pathViews)
  if (smp2) {
    uploadManifestFiles(mainStorage, smp2, manifestFiles, appUserAccess).then(() => {
      console.log('handleUpload_manifest - success', manifestFiles)
      callback({ type: 'updateSuccess' })
    }).catch(error => {
      console.log('handleUpload_manifest - error', error)
      callback({ type: 'updateError', error })
    })
  }
}

const uploadManifestFiles = async (mainStorage, storagePath, manifestFiles, appUserAccess) => {

  const promises = []
  if (manifestFiles) {
    if (_.size(manifestFiles) < 10) {
      _.forEach(manifestFiles, (manifestFile) => {
        manifestFile.metadata = { name: 'test', user: appUserAccess.uid, email: appUserAccess.email }
        const path = `${storagePath}/${manifestFile.name}`
        const storageReference = ref(mainStorage, path);
        promises.push(uploadBytes(storageReference, manifestFile, manifestFile.metadata))
      })
    }
  }
  return Promise.all(promises)
}

const getUploadApprovedList = (fileList, useEmail) => {
  const updateList = {}
  _.forEach(fileList, (item) => {
    const { fileStatusType, file, email, _itemKey } = item
    if (file && fileStatusType === fileStatusTypes.approved) {
      if (useEmail && email) {
        updateList[email] = {
          file: file,
          item: item,
        }
      } else {
        updateList[_itemKey] = {
          file: file,
          item: item,
        }
      }
    }
  })
  return updateList
}
